import React, { useContext, useEffect, useState } from "react";
import HoverTooltip from "../../components/common/HoverTooltip";
import { MapPin, CalendarPlus, Eye, ShieldCheck } from "lucide-react";
import Interactive_Rc from "../../components/support/Interactive_Rc";
import CreateInstallment from "../../components/support/CreateInstallment";
import ViewInstallment from "../../components/support/ViewInstallment";
import Special_Rc from "../../components/support/Special_Rc";
import AppContext from "../../context/AppContext";
import translations from "../../components/common/Translations";
import ReactSelector from "../../components/common/CommonReactSelector";
import { components } from 'react-select';
import { Spinner } from "../../Spinner";
import { getPermissionByScreenCode } from "../../utils/permissions";
import { getActiveDcrcProjects } from "../../service/CustomerSupport";

const Placeholder = (props) => {
    return <components.Placeholder {...props} />;
};

const SupportTabButton = ({ active, number, label, Icon, tooltipText, onClick }) => {
    return (
        <div className="relative w-full min-w-0 lg:w-auto">
            <HoverTooltip text={tooltipText} wrapperClassName="flex h-full w-full min-w-0">
            <button
                type="button"
                onClick={onClick}
                aria-pressed={active}
                className={`grid h-full min-h-12 w-full min-w-0 grid-cols-[20px_minmax(0,1fr)_14px] items-center gap-1 rounded-lg px-1.5 py-2 text-left text-[10px] font-semibold leading-tight transition-all duration-150 sm:flex sm:min-h-10 sm:gap-2 sm:px-3 sm:text-xs lg:w-auto lg:px-4 lg:text-sm lg:whitespace-nowrap ${active
                    ? "bg-[#236DB4] text-white shadow-sm shadow-[#236DB4]/25"
                    : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                    }`}
            >
                <span
                    className={`flex items-center justify-center h-5 w-5 rounded-full text-[11px] font-bold shrink-0 transition-colors ${active ? "bg-white/25 text-white" : "bg-slate-100 text-slate-500"
                        }`}
                >
                    {number}
                </span>
                <span className="min-w-0 break-words sm:flex-1 lg:flex-none">{label}</span>
                <Icon size={15} className={`h-3.5 w-3.5 shrink-0 sm:h-[15px] sm:w-[15px] ${active ? "text-white" : "text-slate-400"}`} />
            </button>
            </HoverTooltip>
        </div>
    );
};

// Sakshi - 2026-08-03: /project/active-dcrc-projects returns { projectId,
// projectName, rcWorkflowId }. rcWorkflowId is the workflow the RC screens use
// to load field agents. The organization id is not part of that row, so it
// stays on the globally selected org.
const buildProjectOption = (project) => {
    const projectId = project?.projectId ?? project?.id;
    const workflowId = project?.rcWorkflowId;
    return {
        label: project?.projectName ?? `Project ${projectId ?? ""}`.trim(),
        value: projectId === undefined || projectId === null ? "" : String(projectId),
        projId: projectId === undefined || projectId === null ? undefined : String(projectId),
        workflowId: workflowId === undefined || workflowId === null ? undefined : String(workflowId),
    };
};

const DASHBOARDS = {
    INTERACTIVE_RC: "interactive_rc",
    INSTALLMENT_PLAN: "installment_plan",
    VIEW_INSTALLMENT: "view_installment",
    SPECIAL_RC: "special_rc",
};

const CustomerSupportDashboard = () => {
    const [activeDashboard, setActiveDashboard] = useState(DASHBOARDS.INTERACTIVE_RC);
    const customVarPerm = getPermissionByScreenCode("CUSTOMER_SUPPORT");
    const [loading, setLoading] = useState(false);
    const { langMode, theme, departmentId } = useContext(AppContext)
    const [activity, setActivity] = useState()
    // Header-slot hand-off: View Installment owns its search/print controls'
    // state, but they render up here next to the heading -- same idea as
    // ExportData.js's setHeaderActivitySelect, scoped locally to this page.
    const [viewInstallmentHeaderActions, setViewInstallmentHeaderActions] = useState(null);
    // Same hand-off for Create Installment Plan's Print button.
    const [createInstallmentHeaderActions, setCreateInstallmentHeaderActions] = useState(null);
    const [interactiveRcHeaderActions, setInteractiveRcHeaderActions] = useState(null);
    const [specialRcHeaderActions, setSpecialRcHeaderActions] = useState(null);
    const switchDashboard = (dashboard) => {
        setLoading(true);
        setTimeout(() => {
            setActiveDashboard(dashboard);
            setLoading(false);
        }, 300);
    };

    const [organizationId, setOrganizationId] = useState();
    // Sakshi - 2026-08-03: Project list now comes from
    // GET /project/active-dcrc-projects instead of a hardcoded DCRC list.
    const [options, setOptions] = useState([]);
    const [projectsLoading, setProjectsLoading] = useState(false);

    const savedOrgId = localStorage.getItem("selectedOrgId");
    useEffect(() => {
        if (savedOrgId) {
            setOrganizationId(JSON.parse(savedOrgId));
        }
    }, [savedOrgId]);

    useEffect(() => {
        let cancelled = false;

        const loadProjects = async () => {
            if (!departmentId) {
                setOptions([]);
                return;
            }
            setProjectsLoading(true);
            try {
                const projects = await getActiveDcrcProjects(departmentId);
                if (cancelled) return;
                setOptions((projects || []).filter(Boolean).map(buildProjectOption));
            } catch (error) {
                if (!cancelled) setOptions([]);
            } finally {
                if (!cancelled) setProjectsLoading(false);
            }
        };

        loadProjects();
        return () => {
            cancelled = true;
        };
    }, [departmentId]);

    const handleProjectChange = (selectedOption) => {
        try {
            const prjctId = selectedOption?.projId;
            const workflowId = selectedOption?.workflowId;

            setActivity(undefined);
            if (!prjctId) return;

            localStorage.setItem("CustprojectId", prjctId);
            if (organizationId !== undefined && organizationId !== null) {
                localStorage.setItem("CustOrgId", organizationId);
            }

            // Sakshi - 2026-08-03: rcWorkflowId drives the field-agent list in the
            // RC screens (fetchAgentByWorkflow).
            if (!workflowId) {
                console.warn("Selected project has no rcWorkflowId:", prjctId);
                return;
            }
            setActivity(workflowId);
        } catch (error) {
            console.error("Error selecting project:", error);
        }
    };

       useEffect(() => {
         setLoading(true);

         const timer = setTimeout(() => {
           setLoading(false);
         }, 500);

         return () => clearTimeout(timer);
       }, []);

    const themeClass =
        theme === "dark" ? "dark-mode" : theme === "high-contrast" ? "high-contrast" : "";

    const supportTabs = [
        {
            id: DASHBOARDS.INTERACTIVE_RC,
            label: translations[langMode].InteractiveRc,
            Icon: MapPin,
            tooltipText: translations[langMode]?.interactiveRcTabTooltip ?? "Submit an interactive RC request with live location",
            subtitle: translations[langMode]?.interactiveRcSubtitle ?? "Submit an interactive RC request with live location.",
        },
        {
            id: DASHBOARDS.INSTALLMENT_PLAN,
            label: translations[langMode].createInstallment,
            Icon: CalendarPlus,
            tooltipText: translations[langMode]?.createInstallmentTabTooltip ?? "Create a new installment plan for an account",
            subtitle: translations[langMode]?.createInstallmentSubtitle ?? "Set up a new installment plan for a customer account.",
        },
        {
            id: DASHBOARDS.VIEW_INSTALLMENT,
            label: translations[langMode].Viewinstallment,
            Icon: Eye,
            tooltipText: translations[langMode]?.viewInstallmentTabTooltip ?? "Browse existing installment plans",
            subtitle: translations[langMode]?.viewInstallmentSubtitle ?? "Look up an account to see its installment plan.",
        },
        {
            id: DASHBOARDS.SPECIAL_RC,
            label: translations[langMode].SpecialRc,
            Icon: ShieldCheck,
            tooltipText: translations[langMode]?.specialRcTabTooltip ?? "Submit a special/exception RC request",
            subtitle: translations[langMode]?.specialRcSubtitle ?? "Submit a special or exception RC request.",
        },
    ];

    const activeTabInfo = supportTabs.find((tab) => tab.id === activeDashboard);

    return (
        <div className={`flex h-full min-h-0 w-full min-w-0 max-w-full flex-col overflow-hidden ${themeClass}`}>
            {loading && <Spinner />}
            <div
                className={`mx-1.5 mt-1.5 flex shrink-0 min-w-0 flex-col gap-3 rounded-xl border border-slate-200 bg-white px-2.5 py-3 shadow-sm sm:mx-4 sm:mt-3 sm:gap-4 sm:px-5 sm:py-4 ${themeClass}`}
            >
                <div className="flex min-w-0 flex-col items-stretch gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div
                        className="grid w-full min-w-0 grid-cols-2 gap-1 rounded-xl border border-slate-200 bg-white p-1.5 shadow-sm dark:border-gray-700 dark:bg-gray-800 md:grid-cols-4 lg:flex lg:w-auto lg:flex-wrap lg:items-center"
                        role="group"
                        aria-label={translations[langMode]?.support ?? "Customer support actions"}
                    >
                        {supportTabs.map(({ id, label, Icon, tooltipText }, i) => (
                            <SupportTabButton
                                key={id}
                                active={activeDashboard === id}
                                number={i + 1}
                                label={label}
                                Icon={Icon}
                                tooltipText={tooltipText}
                                onClick={() => switchDashboard(id)}
                            />
                        ))}
                    </div>
                    <div className="h-auto w-full min-w-0 lg:max-w-[240px] lg:shrink-0">
                        <ReactSelector
                            options={options || []}
                            components={{ Placeholder }}
                            placeholder={translations[langMode].project}
                            onChange={handleProjectChange}
                            isLoading={projectsLoading}
                            isDisabled={projectsLoading || !options.length}
                            height="36px"
                            classNamePrefix="select"
                            classNames={{
                                control: () => "!h-10 !min-h-[40px] !rounded-[10px] !border !border-gray-300 !bg-white",
                                menu: () => "!rounded-[10px] !overflow-hidden",
                                valueContainer: () => "!px-3",
                            }}
                        />
                    </div>
                </div>
                <div className="flex min-w-0 flex-col items-stretch justify-between gap-3 sm:flex-row sm:items-start sm:flex-wrap">
                    <div className="min-w-0 flex-1 basis-auto sm:basis-52">
                        <h1 className="text-base font-bold text-slate-800 leading-tight sm:text-lg">
                            {activeTabInfo?.label ?? translations[langMode].support}
                        </h1>
                        <p className="text-xs text-slate-500 mt-0.5">
                            {activeTabInfo?.subtitle ?? translations[langMode]?.supportSubtitle ?? "Manage RC requests, installment plans, and account updates."}
                        </p>
                    </div>
                    {activeDashboard === DASHBOARDS.INTERACTIVE_RC && interactiveRcHeaderActions}
                    {activeDashboard === DASHBOARDS.VIEW_INSTALLMENT && viewInstallmentHeaderActions}
                    {activeDashboard === DASHBOARDS.INSTALLMENT_PLAN && createInstallmentHeaderActions}
                    {activeDashboard === DASHBOARDS.SPECIAL_RC && specialRcHeaderActions}
                </div>
            </div>
            <div className="flex-1 min-h-0 w-full flex flex-col">
                {activeDashboard === DASHBOARDS.VIEW_INSTALLMENT ? (
                    <div className={`mx-1.5 mb-1.5 mt-1.5 flex min-h-0 min-w-0 flex-1 flex-col gap-3 overflow-y-auto overflow-x-hidden sm:mx-4 sm:mb-3 sm:mt-3 ${themeClass}`}>
                        <ViewInstallment onHeaderActionsChange={setViewInstallmentHeaderActions} />
                    </div>
                ) : (
                    <div
                        className={`mx-1.5 mb-1.5 mt-1.5 flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm sm:mx-4 sm:mb-3 sm:mt-3 ${themeClass}`}
                    >
                        <div className="min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden p-3 sm:p-5 xl:p-6">
                            {activeDashboard === DASHBOARDS.INTERACTIVE_RC && (
                                <Interactive_Rc
                                    activityId={activity}
                                    customVarPerm={customVarPerm?.canCreate}
                                    onHeaderActionsChange={setInteractiveRcHeaderActions}
                                />
                            )}
                            {activeDashboard === DASHBOARDS.INSTALLMENT_PLAN && (
                                <CreateInstallment
                                    customVarPerm={customVarPerm?.canCreate}
                                    onHeaderActionsChange={setCreateInstallmentHeaderActions}
                                />
                            )}
                            {activeDashboard === DASHBOARDS.SPECIAL_RC && (
                                <Special_Rc
                                    activityId={activity}
                                    customVarPerm={customVarPerm?.canCreate}
                                    onHeaderActionsChange={setSpecialRcHeaderActions}
                                />
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CustomerSupportDashboard;
